import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  School,
  Business,
  Paid,
  Public,
  TrendingUp,
  Diversity3,
  WorkspacePremium,
  Lightbulb,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const Careers = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);

  const benefits = [
    {
      icon: <Paid />,
      title: 'Competitive Pay',
      desc: 'High-paying roles designed to reward talent and skill',
      color: '#e60108',
    },
    {
      icon: <School />,
      title: 'Learning & Development',
      desc: 'Continuous training and upskilling opportunities',
      color: '#191919',
    },
    {
      icon: <TrendingUp />,
      title: 'Career Growth',
      desc: 'Clear pathways to grow with a fast-scaling company',
      color: '#e60108',
    },
    {
      icon: <Diversity3 />,
      title: 'Inclusive Culture',
      desc: 'A workplace that values every voice and background',
      color: '#191919',
    },
    {
      icon: <Public />,
      title: 'Regional Impact',
      desc: 'Be part of building connectivity across Southern Africa',
      color: '#e60108',
    },
    
  ];

  const commitments = [
    { text: 'Creating meaningful jobs in Eswatini and beyond', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
    { text: 'Competitive salaries that reflect real value', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
    { text: 'Upskilling local talent for long-term careers', icon: <CheckCircle sx={{ fontSize: 18 }} /> },
  ];

  const handleApplyClick = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Join Our Team.jpg"
          alt="Join Our Team"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Careers"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                display: 'inline-flex',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Join Our Team
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 750,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Help us build secure internet for Southern Africa — and create
              high-paying jobs that reduce unemployment across the region.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Mission-Driven Employment Section */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Commitment"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              More Than a Job — <br />
              <Box component="span" sx={{ color: '#e60108' }}>
                A Mission
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                lineHeight: 1.9,
                fontSize: '1.05rem',
                maxWidth: 780,
                mx: 'auto',
                mb: 4,
              }}
            >
              We are building a company that doesn't just connect people — it
              creates real opportunities. Our goal is to provide meaningful,
              high-paying employment in Eswatini and across Southern Africa,
              helping tackle unemployment and empowering the next generation
              of skilled professionals.
            </Typography>

            <Stack
              spacing={2}
              sx={{ mt: 3, maxWidth: 600, mx: 'auto', alignItems: 'center' }}
            >
              {commitments.map((item, index) => (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent="center"
                  key={index}
                  sx={{ width: '100%' }}
                >
                  <Box sx={{ color: '#e60108', display: 'flex' }}>{item.icon}</Box>
                  <Typography variant="body2" sx={{ color: '#191919', fontWeight: 500 }}>
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </motion.div>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Why Join Us"
              sx={{
                mb: 2,
                bgcolor: '#e6010810',
                color: '#e60108',
                fontWeight: 600,
                mx: 'auto',
                display: 'table',
              }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Why Join Us?
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}
            >
              Amazing benefits for amazing people
            </Typography>
          </motion.div>

          <Grid
            container
            spacing={3}
            justifyContent="center"
            alignItems="stretch"
          >
            {benefits.map((benefit, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={index}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                  style={{ width: '100%', display: 'flex' }}
                >
                  <Card
                    sx={{
                      width: '100%',
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: benefit.color + '15',
                        color: benefit.color,
                        margin: '0 auto 16px',
                      }}
                    >
                      {benefit.icon}
                    </Avatar>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ color: '#191919', textAlign: 'center' }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#a0a0a0', textAlign: 'center', lineHeight: 1.6 }}
                    >
                      {benefit.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Join Our Talent Community CTA */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Card
              sx={{
                p: { xs: 4, md: 5 },
                borderRadius: 4,
                background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)',
                color: '#fefefe',
                textAlign: 'center',
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: 'rgba(254,254,254,0.15)',
                  color: '#fefefe',
                  margin: '0 auto 20px',
                }}
              >
                <WorkspacePremium sx={{ fontSize: 36 }} />
              </Avatar>
              <Typography variant="h3" gutterBottom fontWeight="800">
                Join Our Talent Community
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9, maxWidth: 520, mx: 'auto', fontWeight: 400 }}
              >
                Don't see the perfect role? Send us your resume and we'll keep
                you in mind for future opportunities.
              </Typography>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                style={{ display: 'inline-block' }}
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleApplyClick}
                  sx={{
                    bgcolor: '#fefefe',
                    color: '#e60108',
                    px: 5,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    '&:hover': {
                      bgcolor: '#fefefe',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Send Your Resume
                </Button>
              </motion.div>
            </Card>
          </motion.div>
        </Container>
      </Box>

      <CTASection
        title="Questions About Careers?"
        subtitle="Contact our HR team to learn more about working with us"
        buttonText="Contact HR"
        buttonLink="/contact"
      />

      {/* Application Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#e60108', color: '#fefefe' }}>
          <Typography variant="h6" fontWeight="bold">
            Join Our Talent Community
          </Typography>
          <Typography variant="body2" sx={{ color: '#fefefe', opacity: 0.9 }}>
            Send us your resume and we'll contact you when opportunities arise
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField fullWidth label="Full Name" required />
            <TextField fullWidth label="Email" type="email" required />
            <TextField fullWidth label="Phone" required />
            <TextField fullWidth label="LinkedIn Profile" />
            <TextField
              fullWidth
              label="Tell us about your skills and experience"
              multiline
              rows={4}
              placeholder="Share your background, expertise, and what role you're looking for..."
            />
            <Button
              variant="outlined"
              component="label"
              sx={{
                borderColor: '#e60108',
                color: '#e60108',
                '&:hover': { borderColor: '#b80006', bgcolor: '#e6010810' },
              }}
            >
              Upload Resume (PDF, DOC)
              <input type="file" hidden accept=".pdf,.doc,.docx" />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#a0a0a0' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            sx={{ bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Careers;